import { Injectable } from "@angular/core";
import { Observable } from "rxjs/Observable";
import { AngularFireDatabase } from "angularfire2/database";
import "rxjs/add/observable/zip";
import { BillTypeService } from "../bill/bill-type.service";

@Injectable()
export class PaymentService {

  constructor(private db: AngularFireDatabase, private billTypeService: BillTypeService) {
  }

  getPayments(communityId, status?): Observable<Array<{}>> {
    return this.db.list(`/payments/${communityId}`).valueChanges()
      .map(data => {
        let payments = [];
        data.map(payment => payments = payments.concat(Object.values({ ...payment })));
        if (status) { payments = payments.filter((payment: any) => status === payment.status); }

        return payments.length ?
          payments.map((payment: any) => this.formatPaymentDetails(communityId, payment)) : payments;
      }).flatMap(payment => {
        return payment.length ? Observable.combineLatest(payment) : Observable.of(payment);
      });
  }

  private getProfile(userId): Observable<{}> {
    return this.db.object(`/profile/${userId}`).valueChanges();
  }

  private formatPaymentDetails(communityId, payment): Observable<{}> {
    const resident$ = this.getProfile(payment.residentId);
    const verifiedBy$ = this.getProfile(payment.verifiedBy);
    const billType$ = this.billTypeService.getBillTypesFromSubscription(communityId, payment.residentId, payment.subscriptionId);

    return Observable.zip(resident$, billType$, verifiedBy$, (resident: any, billType, verifier: any) => {
        return {
          resident: resident ? `${resident.firstName} ${resident.lastName}` : "",
          billType,
          status: payment.status,
          date: payment.createdAt,
          amount: payment.amount,
          method: payment.method,
          subscriptionId: payment.subscriptionId,
          residentId: payment.residentId,
          id: payment.id,
          comment: payment.comment,
          structure: payment.structure,
          tellerNumber: payment.tellerNumber,
          plan: payment.servicePlan ? payment.servicePlan.name : "",
          price: payment.servicePlan ? payment.servicePlan.price :
            payment.priceDetail ? payment.priceDetail.minPrice : "",
          verifiedBy: verifier ? `${verifier.firstName} ${verifier.lastName}` : "",
          verificationDate: payment.verificationDate,
          paymentReference: payment.paymentReference,
          tranxReference: payment.tranxReference
        };
    });
  }

  async verifyPayment(communityId, payment, status, comment, userId): Promise<{message: string, status: boolean}> {
    comment = comment || "";
    const verifiedBy = userId;
    const verificationDate = Date.now();
    let response: {message: string, status: boolean} ;
    response = {message: "", status: true};

    try {
      if (status !== "pending") {
        const data = {};
        data["status"] = status;
        data["comment"] = comment;
        data["verifiedBy"] = verifiedBy;
        data["verificationDate"] = verificationDate;

        const residentId = payment["residentId"];
        const subscriptionId = payment["subscriptionId"];

        const subscription = await new Promise<any>(resolve => {
          this.db.object(`/subscriptions/${communityId}/${residentId}/${subscriptionId}`)
          .valueChanges().subscribe(payload => resolve(payload));
        });

        const payments = await new Promise<any>(resolve => {
          this.db.object(`/payments/${communityId}/${residentId}`).valueChanges()
          .subscribe(payload => {
            if (payload) {
              const items = Object.values(payload);
              resolve(items.filter(item => item.subscriptionId === subscriptionId
              && (item.status === "full" || item.status === "part")));
            } else {
              resolve([]);
            }
          });
        });

        let totalVerifiedPayment = 0;
        for (const eachPayment of payments) {
          totalVerifiedPayment += eachPayment.amount;
        }

        const subscriptionAdvance = Number(subscription.advance);
        const paidAmount = Number(payment.amount);

        if (subscriptionAdvance === paidAmount && (subscription.amount - totalVerifiedPayment)  === paidAmount) {
          // this signifies full payment ...
          if (status === "part") {
            response.status = false;
            response.message = "This subscription is fully paid for, kindly verify as 'Full Payment'!";

            return response;
          }
        } else {
          // this signifies part payment ...
          if (status === "full") {
            response.status = false;
            response.message = "This subscription is partly paid for, kindly verify as 'Part Payment'!";

            return response;
          }
        }

        await this.db.list(`/payments/${communityId}/${residentId}`).update(payment.id, data);
        const subscriptionUpdates = {};

        if (status !== "failed") {
          subscriptionUpdates["isActive"] = status === "full" || status === "part";
          subscriptionUpdates["isPaid"] = status === "full" || status === "part";
          subscriptionUpdates["balance"] = subscription.amount - (totalVerifiedPayment + paidAmount);
        }

        subscriptionUpdates["advance"] = subscriptionAdvance >= paidAmount ?
          subscriptionAdvance - paidAmount : subscriptionAdvance;

        await this.db.list(`/subscriptions/${communityId}/${residentId}`).update(subscriptionId, subscriptionUpdates);
        response.message = "Payment verification successful";

        return response;
      }
    } catch (e) {
      console.log(e);
      response.status = false;
    }

    return response;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/payment/payment.service.ts